import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Wrench, ChevronRight } from "lucide-react";

const statusLabels = {
  appareil_depose: "Appareil déposé",
  en_attente_diagnostic: "En attente diagnostic",
  diagnostic_en_cours: "Diagnostic en cours",
  devis_en_cours: "Devis en cours",
  devis_envoye: "Devis envoyé",
  devis_accepte: "Devis accepté",
  devis_refuse: "Devis refusé",
  en_attente_pieces: "En attente de pièces",
  reparation_en_cours: "Réparation en cours",
  reparation_terminee: "Réparation terminée",
  pret_a_restituer: "Prêt à restituer",
  restitue: "Restitué",
  annule: "Annulé",
};

const statusColors = {
  appareil_depose: "bg-blue-100 text-blue-800",
  diagnostic_en_cours: "bg-yellow-100 text-yellow-800",
  en_attente_diagnostic: "bg-yellow-100 text-yellow-800",
  devis_en_cours: "bg-orange-100 text-orange-800",
  devis_envoye: "bg-purple-100 text-purple-800",
  devis_accepte: "bg-green-100 text-green-800",
  devis_refuse: "bg-red-100 text-red-800",
  en_attente_pieces: "bg-orange-100 text-orange-800",
  reparation_en_cours: "bg-cyan-100 text-cyan-800",
  reparation_terminee: "bg-teal-100 text-teal-800",
  pret_a_restituer: "bg-green-100 text-green-800",
  restitue: "bg-gray-100 text-gray-800",
  annule: "bg-red-100 text-red-800",
};

const ClientPortalSAVCases = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCases = async () => {
      try {
        const res = await API.get("/portal/sav-cases");
        setCases(res.data);
      } catch (error) {
        toast.error("Erreur lors du chargement des dossiers");
      } finally {
        setLoading(false);
      }
    };
    fetchCases();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-portal-sav-cases">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Mes dossiers SAV</h1>
        <p className="text-muted-foreground">Historique de toutes vos réparations</p>
      </div>

      {cases.length > 0 ? (
        <div className="space-y-3">
          {cases.map((c) => (
            <Card
              key={c.id}
              className="hover:bg-secondary/30 cursor-pointer transition-colors"
              onClick={() => navigate(`/portal/sav/${c.id}`)}
              data-testid={`sav-case-row-${c.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <Wrench className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{c.numero}</p>
                      <p className="text-sm text-muted-foreground">
                        {c.device?.marque} {c.device?.modele}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Créé le {new Date(c.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={statusColors[c.statut] || "bg-gray-100 text-gray-800"}>
                      {statusLabels[c.statut] || c.statut}
                    </Badge>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Wrench className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="font-medium">Aucun dossier SAV</p>
            <p className="text-sm text-muted-foreground">Vos dossiers de réparation apparaîtront ici</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientPortalSAVCases;
